import '../api/models/types.d.ts'
import { Controller } from '@hotwired/stimulus'
import { log } from '../helpers/debug'
import componentTypes from '../helpers/componentTypes'
import ComputedField from '../helpers/computedFields'
import type { ComputedField as ComputedFieldType } from '../api/models/types'
import { handleRequest } from '../api/handleRequest'
import { passesConditions } from '../api/helpers/conditions'
import { debounce } from '../helpers/debounce'
import { componentPassesValidation } from '../helpers/validation'
import ActionManager from '../helpers/actions'
import {
  loadUserData,
  preloadPageImages,
  scrollToTop,
  focusTopOfPage,
  setResponsiveWatchers,
} from '../helpers/controllerHelpers/flow'
import writeUserData from '../helpers/controllerHelpers/writeUserData'
import resetUserData from '../helpers/resetUserData'
import { track } from '../helpers/controllerHelpers/savvyAnalytics'
import jitsu from '../helpers/jitsu'
import {
  localStorageGet,
  sessionStorageGet,
  sessionStorageSet,
  sessionStorageRemove,
  injectHighlight,
  injectPostHog,
  initAndWaitForSupabaseClient,
  isBuilderEditingMode,
  getRendererBase,
  hasUrlParam,
  createAndInsertCustomFontTag,
  deepFreeze,
} from '../helpers/utils'
import {
  getOpenInfoBoxFn,
  getCloseInfoBoxFn,
  connectUserData,
  updateFlowUserData,
} from '../helpers/controllerHelpers/utils'
import { setValidationMessage } from '../helpers/controllerHelpers/customValidationUI'
import getUrlDict from '../helpers/getUrlDict'
import getLocationData from '../helpers/locationData'
import { env } from '../helpers/env'
import { generateBodyHtml } from '../api/renderHtml'
import stylesGenerator from '../api/styles/generator'
import { Database } from 'firebase-firestore-lite'
import {
  countSplitKeys,
  getSplitKeyCountFromLocalStorage,
  inspectSavvyFormUserDataRead,
  logSentryStep,
} from '../helpers/sentryObservability'
import { inspectStorageSentinel } from '../helpers/storageSentinel'
import {
  inspectExperimentCookie,
  inspectWindowFallback,
  recoverExperimentSplitsFromCookie,
  recoverExperimentSplitsFromWindow,
} from '../helpers/experimentCookie'

import type GlobalController from './global_controller'

import type {
  Flow,
  FlowPage as Page,
  CustomHTMLElement,
  CustomEvent,
  Component,
  UserData,
  UserDataValue,
  OptionSelector,
  State,
  History,
  OptionSelectorButton,
  VersionMetadata,
  CustomButton,
  Context,
  ResetUserDataOptions,
  TriggerError,
  TagFontMetadata,
} from '../api/models/types'
import { isEqual, cloneDeep } from 'lodash'
import replaceStorageURLsFromFirebaseToCloudflare from '../api/helpers/replaceStorageURLsFromFirebaseToCloudflare.ts'

interface BaseComponent {
  set_value_class?: boolean
  type: string
  key: string
  id: string
  hide?: boolean
  conditions: unknown[]
}

interface CustomElement extends HTMLElement {
  customValidityMessage: string
}

interface ReactivelyUpdateUserDataOptions {
  bypassDebounce?: boolean
}

interface Update {
  current_page_id: string
  current_page_index: number
  current_page_key: string
  highest_page_reached_id?: string
  highest_page_reached_key?: string
  highest_page_reached_index?: number
}

interface BaseComponent {
  set_value_class?: boolean
  type: string
  key: string
  id: string
  hide?: boolean
  conditions: unknown[]
}

interface PageComponent extends BaseComponent {
  buttons?: BaseComponent[]
}

interface Update {
  current_page_id: string
  current_page_index: number
  current_page_key: string
  highest_page_reached_id?: string
  highest_page_reached_key?: string
  highest_page_reached_index?: number
}

const CONTROLLERS_WITH_USER_DATA = new Set(['condition', 'template', 'components--option-selector'])
const CONTROLLERS_WITH_REQUIREMENTS = new Set([
  'components--custom-button',
  'components--stripe-checkout',
  'components--stripe-checkout-2',
  'components--paypal-checkout',
])

export default class extends Controller {
  db: Database
  static classes = ['default']

  declare readonly defaultClasses: Set<string>

  static targets = ['form', 'infobox']

  declare readonly formTarget: CustomHTMLElement | null
  declare readonly infoboxTarget: CustomHTMLElement | null
  declare readonly hasInfoboxTarget: boolean
  declare readonly hasFormTarget: boolean

  static values = {
    url: String,
    pageId: String,
    id: String,
    version: String,
    groupId: String,
    requirementsMet: Boolean,
    runtimeId: String,
  }

  declare readonly urlValue: string
  declare pageIdValue: string
  declare upToDateUserData: Partial<UserData>
  declare readonly idValue: string
  declare readonly versionValue: string
  declare readonly branchIdValue: string
  declare readonly groupIdValue: string
  declare readonly projectIdValue: string
  declare requirementsMetValue: boolean
  declare runtimeIdValue: string

  declare UPDATE_NUM: number
  declare hasConnected: boolean
  declare getFlow: Function
  declare setFlow: Function
  declare flowUpdateCallbacks: Array<Function>
  declare flowVersionCallbacks: Array<Function>

  prefilledKeys: Set<string>
  computedFields: ComputedField[]
  pages: Page[]
  preloadedImages: HTMLLinkElement[]
  actions: ActionManager
  private _destroyObserver: () => void
  updateUserData: (...args: UserDataValue[]) => void
  flow: Flow
  projectId?: string
  pagesById: Record<string, Page>
  nextPageTimeout: NodeJS.Timeout
  infoBoxPageId?: string | null
  awaitingNext: boolean
  awaitingPageChange: boolean
  evalId: number
  syncPagesWithUrl?: boolean
  beforeUnloadHandler: () => void
  reset_css?: boolean
  hasLoadedFirstFlowPage: boolean
  private _pendingPromise?: Promise<void | boolean>
  private _promiseActions: {
    resetTimeout: () => void
    resolve: () => void
    reject: () => void
  } | null
  private _promiseId: number
  disconnectUserData: () => void
  userDataValue: Partial<UserData>
  previewUpdateMode: string
  declare isReactBuilder: boolean
  declare safeModeValue: boolean
  private hasLoggedFirstReactiveUpdate = false

  async connect() {
    this.runtimeIdValue = Math.random().toString().slice(2)
    this.hasLoggedFirstReactiveUpdate = false
    const flowId = this.idValue

    logSentryStep({
      scope: 'Flow controller',
      functionName: 'connect',
      step: 'connect_start',
      props: {
        flow_id: flowId,
        runtime_id: this.runtimeIdValue,
      },
    })

    const self = this
    const urlParams = getUrlDict() as Record<string, unknown>
    this.UPDATE_NUM = 1
    this.flowUpdateCallbacks = []
    this.flowVersionCallbacks = []

    this.isReactBuilder = hasUrlParam('savvy_editing', 'react_builder')
    this.safeModeValue = hasUrlParam('embeddables_safe_mode', 'true')

    track(this.idValue, 'began_flow')
    // jitsu.track('flow:viewed')
    this.prefilledKeys = new Set()
    this.computedFields = []
    this.pages = []
    this.preloadedImages = []
    this.flow = window.SavvyFlow[this.idValue] || {}

    logSentryStep({
      scope: 'Flow controller',
      functionName: 'connect',
      step: 'savvy_flow_read',
      props: {
        flow_id: flowId,
        runtime_id: this.runtimeIdValue,
        savvy_flow_present: Object.keys(this.flow).length > 0,
        has_pages: Array.isArray(this.flow.pages) && this.flow.pages.length > 0,
      },
    })

    this.projectId = window.Savvy.flow_metadata?.[this.idValue]?.project_id || this.flow.project_id

    jitsu.setup(this.groupIdValue, this.projectId || '')
    jitsu.set({
      group_id: this.groupIdValue,
    })
    jitsu.setForFlow(this.idValue, {
      flow_id: this.idValue,
      flow_version: this.versionValue,
      is_live_flow_version: '', // @TODO
    })
    log('this.projectId', this.projectId)
    if (this.projectId && !shouldDisableSessionReplay(this.projectId))
      jitsu.startRecordingSessionReplay(this.projectId)

    // if (this.flow) delete window.SavvyFlow[this.idValue]
    // this.loadFlow();
    const userDataReadInspection = inspectSavvyFormUserDataRead({ flowId })
    const sentinelInspection = inspectStorageSentinel()
    const experimentCookieInspection = inspectExperimentCookie({ flowId })
    const windowFallbackInspection = inspectWindowFallback({ flowId })
    const evalWriteAt = window.__EMBEDDABLES_EVAL_WRITE_AT__?.[flowId]
    logSentryStep({
      scope: 'Flow controller',
      functionName: 'connect',
      step: 'load_user_data_before',
      props: {
        flow_id: flowId,
        runtime_id: this.runtimeIdValue,
        timestamp: Date.now(),
        local_storage_split_key_count: getSplitKeyCountFromLocalStorage({ flowId }),
        storage_read_ok: userDataReadInspection.storage_read_ok,
        storage_read_error: userDataReadInspection.storage_read_error,
        storage_read_result: userDataReadInspection.storage_read_result,
        sentinel_present: sentinelInspection.sentinel_present,
        sentinel_written_at: sentinelInspection.sentinel_written_at,
        sentinel_read_error: sentinelInspection.sentinel_read_error,
        experiment_cookie_present: experimentCookieInspection.experiment_cookie_present,
        experiment_cookie_split_key_count:
          experimentCookieInspection.experiment_cookie_split_key_count,
        experiment_cookie_parse_ok: experimentCookieInspection.experiment_cookie_parse_ok,
        window_fallback_present: windowFallbackInspection.window_fallback_present,
        window_fallback_split_key_count: windowFallbackInspection.window_fallback_split_key_count,
        eval_to_connect_ms: typeof evalWriteAt === 'number' ? Date.now() - evalWriteAt : null,
      },
    })

    this.userDataValue = {}
    //* When SavvyFormUserData is gone but the experiment cookie survives, repair storage before
    //* loadUserData so the rest of the session (and split_persist_check) stay on the healthy path.
    const experimentCookieRecovery = recoverExperimentSplitsFromCookie({
      flowId,
      storageReadResult: userDataReadInspection.storage_read_result,
      storeExperimentsInCookies: this.flow.store_experiments_in_cookies,
      doNotStoreAnyData: this.flow.do_not_store_any_data,
    })
    //* Window fallback only runs when the cookie lane did not already finalize recovery
    //* (`cookie_recovered`) or report a terminal write failure (`cookie_write_failed`).
    //* Otherwise window recovery could overwrite a cookie outcome in Sentry even though
    //* cookie data was present.
    const experimentSplitRecovery =
      experimentCookieRecovery.splitSource === 'cookie_recovered' ||
      experimentCookieRecovery.splitSource === 'cookie_write_failed'
        ? experimentCookieRecovery
        : recoverExperimentSplitsFromWindow({
            flowId,
            storageReadResult: userDataReadInspection.storage_read_result,
            doNotStoreAnyData: this.flow.do_not_store_any_data,
          })
    loadUserData(this, this.idValue)

    logSentryStep({
      scope: 'Flow controller',
      functionName: 'connect',
      step: 'load_user_data_after',
      props: {
        flow_id: flowId,
        runtime_id: this.runtimeIdValue,
        timestamp: Date.now(),
        user_data_split_key_count: countSplitKeys({ data: this.userDataValue }),
        split_source: experimentSplitRecovery.splitSource,
        recovered_split_key_count: experimentSplitRecovery.recoveredSplitKeyCount,
      },
    })

    this.disconnectUserData = connectUserData(this)
    this.loadComputedFields()
    // await this.loadComputedFields()
    this.actions = new ActionManager(this.flow, this, {
      groupId: this.groupIdValue,
      flowId: this.idValue,
    })

    this._destroyObserver = setResponsiveWatchers(
      this.element as HTMLElement,
      this.flow.breakpoints
    )
    this.evaluateConditionsForFlow()

    this.syncPagesWithUrl = this.flow.sync_pages_with_url || (urlParams.savvy_url_sync as boolean)
    this.reset_css = !!this.flow.reset_css

    this.hasConnected = true

    logSentryStep({
      scope: 'Flow controller',
      functionName: 'connect',
      step: 'connect_complete',
      props: {
        flow_id: flowId,
        runtime_id: this.runtimeIdValue,
      },
    })

    /* Trigger onLoad / onView outputs - NYI - popup conditional for onview */
    const onLoadFlowActions = this.flow.outputs_onloadflow || []
    const onViewFlowActions = this.flow.outputs_onviewflow || []

    onLoadFlowActions.forEach((id) => this.dispatchAction(id, 'outputs_onloadflow'))
    onViewFlowActions.forEach((id) => this.dispatchAction(id, 'outputs_onloadflow'))
    /* Initialize necessary values */
    this.pageIdValueChanged(this.pageIdValue)

    if (this.syncPagesWithUrl) {
      this.addHistoryListener()
      const page = this.pages.find((p) => p.id === this.pageIdValue)
      const pageIndex = this.pages.findIndex((p) => p.id === this.pageIdValue)
      window.history.replaceState(
        { flowPageKey: page?.key, flowPageIndex: pageIndex },
        '',
        `#${page?.key}`
      )

      this.beforeUnloadHandler = () => {
        sessionStorageRemove('SAVVY_WEB_HISTORY')
      }

      window.addEventListener('beforeunload', this.beforeUnloadHandler)
    }
    this.updateUserData = debounce(this.reactivelyUpdateUserData, 150)
    this.reactivelyUpdateUserData({ bypassDebounce: true })

    this.setFlow = function (flow: Flow) {
      const safePrevFlow = deepFreeze(self.flow)
      self.flow = flow

      //* Protect against accidental mutations - freeze the new flow
      const safeFlow = deepFreeze(flow)

      //* Don't clone per callback - eliminates the clone multiplier
      self.flowUpdateCallbacks.forEach((cb) => cb(safeFlow, safePrevFlow))
      self.evaluateConditionsForFlow()
    }

    this.getFlow = function (raw) {
      const rawFlow = self.flow || {}
      return raw ? rawFlow : { ...rawFlow, pages: self.pages, pagesById: self.pagesById }
    }

    // Location Data Feature
    if (this.flow.use_location_data) {
      const locationData = await getLocationData()
      this.setUserData('location_data', locationData)
    }

    const head = document.querySelector('head')

    ;(this.flow.load_tags || []).map((tag) => {
      //* Verify both type === 'font' and font_type === 'custom' to ensure it's a custom font
      const tagMetadata = tag?.metadata as TagFontMetadata | undefined
      const isCustomFont = tagMetadata?.type === 'font' && tagMetadata?.font_type === 'custom'

      if (isCustomFont) {
        createAndInsertCustomFontTag({ tag })
        return
      }

      //* Regular tag processing
      const tagEl = document.createElement(tag.tag)
      const url = replaceStorageURLsFromFirebaseToCloudflare(tag.url) || ''
      tagEl.setAttribute(tag.tag === 'link' ? 'href' : 'src', url)
      if (tag.attributes)
        Object.entries(tag.attributes).map(([attr, val]) => tagEl.setAttribute(attr, val))
      head?.appendChild(tagEl)
    })

    setTimeout(() => {
      window.Savvy.addEventLogs?.([
        {
          flow_id: this.idValue,
          event_type: 'webpage:viewed',
          timestamp: Date.now(),
          user_data_snapshot: this.userDataValue as UserData,
          data: 'Flow loaded',
        },
      ])
    }, 1000)

    if (this.flow.highlight_project_id && !isBuilderEditingMode()) {
      injectHighlight(this.flow.highlight_project_id)
    }
    if (this.flow.posthog_product_id) {
      injectPostHog(this.flow.posthog_product_id)
    }

    // if live=refresh or rerender we need to suscribe to the channel to get the latest draft or saved version
    if (urlParams.live === 'refresh' || urlParams.live === 'rerender') {
      const embeddableDraftId: string | undefined =
        (urlParams.embeddable_draft as string) || undefined
      this.setUpLiveEditsChannelSubscription(urlParams.live, embeddableDraftId)
    }

    this.syncGlobalSelectedFlowAfterMount()
  }

  /**
   * Cross-embed hosts use `<savvy experiment-id="…">` without a host `id`.
   * global_controller.connect() may run before this flow mounts, so it cannot
   * resolve selectedFlowIdValue from the inner `[data-flow-id-value]` yet.
   *
   * Re-run global_controller.trySyncSelectedFlowFromSavvyHost() once mounted.
   */
  private syncGlobalSelectedFlowAfterMount() {
    const globalEl = document.querySelector('savvy-global')
    if (!globalEl) return

    const controller = this.application.getControllerForElementAndIdentifier(
      globalEl,
      'global'
    ) as GlobalController | null

    controller?.trySyncSelectedFlowFromSavvyHost()
  }

  disconnect() {
    this.dispatchAction(this.flow.outputs_onremoved || [], 'outputs_onremoved')
    this.disconnectUserData()
    // delete window.SavvyFlow[this.idValue]
    this.preloadedImages = this.preloadedImages.reduce((acc, el) => {
      el.remove()
      return acc
    }, [])
    if (typeof this._destroyObserver === 'function') this._destroyObserver()
    if (this.syncPagesWithUrl) {
      this.removeHistoryListener()
      window.removeEventListener('beforeunload', this.beforeUnloadHandler)
    }
  }

  pageIdValueChanged(pageId: string, prevPageId?: string) {
    if (!this.hasConnected) return
    if (
      ((!this.hasInfoboxTarget && this.hasFormTarget) ||
        (this.hasInfoboxTarget && !this.flow.dont_scroll_to_top_popup)) &&
      (this.flow.scroll_to_top_page || this.flow.scroll_to_document_top)
    ) {
      this.scrollToTop()
    }

    const splitId = pageId.split('hidden-')[1]
    pageId = splitId || pageId

    // const currentPageIndex = this.flow.pages.findIndex((p) => p.id === pageId)
    const currentPageIndex = splitId
      ? this.flow.pages.findIndex((p) => p.id === splitId)
      : this.pages.findIndex((p) => p.id === pageId)
    // const currentPage = this.flow.pages.find((p) => p.id === pageId)
    const currentPage = splitId
      ? this.flow.pages.find((p) => p.id === splitId)
      : this.pagesById[pageId]

    if (!currentPage) {
      console.warn(`No page found for page id ${pageId}`)
      return
    }

    const updates: Update = {
      current_page_id: pageId,
      current_page_index: currentPageIndex,
      current_page_key: currentPage.key,
    }

    const newPageOverallIndex = this.flow.pages?.findIndex((p) => p.id === pageId) || 0
    const highestPageOverallIndex =
      this.flow.pages?.findIndex((p) => p.id === this.userDataValue.highest_page_reached_id) || 0
    const isHighestPageYet = newPageOverallIndex >= highestPageOverallIndex

    if (isHighestPageYet) {
      updates.highest_page_reached_id = pageId
      updates.highest_page_reached_key = currentPage.key
      updates.highest_page_reached_index = currentPageIndex

      jitsu.setForFlow(this.idValue, {
        max_page_id: pageId,
        max_page_key: currentPage.key,
        max_page_index: currentPageIndex,
      })
    }

    this.batchUpdate({
      detail: {
        updates: updates as unknown as Record<string, unknown>,
      },
    }).then((userData) => {
      const pageChangedEvent = new CustomEvent('savvy:page_user_data_changed', {
        detail: {
          userData,
        },
      })
      this.element.dispatchEvent(pageChangedEvent)

      const { current_page_id, current_page_key, current_page_index } = userData as any
      window.dispatchEvent(
        new CustomEvent('embeddables:page_change_userdata_updated', {
          detail: {
            current_page_id,
            current_page_key,
            current_page_index,
            previous_page_id: prevPageId,
            previous_page_key: prevPage?.key,
            previous_page_index: prevPageIndex,
          },
        })
      )
    })

    clearTimeout(this.nextPageTimeout)

    const prevPage = this.flow?.pages?.find((p) => p.id === prevPageId) || ({} as Page)

    const editingMode = isBuilderEditingMode(new URL(window.location.href))
    if (!editingMode && currentPage?.next_on_timeout) {
      this.nextPageTimeout = setTimeout(() => this.next(), currentPage.next_on_timeout)
    }

    this.element.querySelectorAll('[data-page-page-id-value]').forEach((el) => {
      el.setAttribute('data-page-page-id-value', pageId)
      el.setAttribute('data-page-page-key-value', currentPage.key)
    })

    this.element
      .querySelectorAll(
        '[data-global-component][data-global-component-location=before_page], [data-global-component][data-global-component-location=after_page]'
      )
      .forEach((el) => {
        el.setAttribute('data-component-page-id-value', pageId)
        el.setAttribute('data-component-page-key-value', currentPage.key)

        //* We need to update the PageKey and PageTag classes on the global components
        if (prevPage?.key) el.classList.remove(`PageKey-${prevPage.key}`)
        if (Array.isArray(prevPage?.tags)) {
          prevPage.tags.forEach((tag) => el.classList.remove(`PageTag-${tag}`))
        }

        el.classList.add(`PageKey-${currentPage.key}`)
        if (Array.isArray(currentPage.tags))
          currentPage.tags.forEach((tag) => el.classList.add(`PageTag-${tag}`))
      })

    const prevPageIndex = this.flow?.pages?.findIndex((p) => p.id === prevPageId) || -1
    this.element.querySelectorAll('[data-element]').forEach((el) => {
      if (prevPage?.key) el.classList.remove(`PageKey-${prevPage.key}`)
      if (Array.isArray(prevPage?.tags)) {
        prevPage.tags.forEach((tag) => el.classList.remove(`PageTag-${tag}`))
      }

      el.classList.add(`PageKey-${currentPage.key}`)
      if (Array.isArray(currentPage.tags))
        currentPage.tags.forEach((tag) => el.classList.add(`PageTag-${tag}`))
    })

    /* Trigger Actions for the new page */
    const actions = currentPage.outputs_onload || []

    actions.forEach((id) => this.dispatchAction(id, 'outputs_onload'))

    this.preloadedImages = preloadPageImages(currentPage.preloadImages, this.preloadedImages)

    //* If auto_preload_all_images is true, preload all images on next page
    if (this.flow.auto_preload_all_images) {
      const nextPageImagesSrc = this.getNextPageImagesSrc()
      this.preloadedImages = preloadPageImages(nextPageImagesSrc, this.preloadedImages)
    }

    track(this.idValue, 'reached_flow_page', { pageId, pageIndex: currentPageIndex })

    jitsu.setForFlow(this.idValue, {
      flow_page_id: pageId,
      flow_page_key: currentPage.key,
      flow_page_index: currentPageIndex,
      flow_total_pages: this.pages.length,
    })
    jitsu.track('flowpage:viewed', {
      flow_id: this.idValue,
      view_type: this.hasLoadedFirstFlowPage ? 'view' : 'load',
    })

    this.hasLoadedFirstFlowPage = true

    if (!window.Savvy) window.Savvy = {}
    window.Savvy.addEventLogs?.([
      {
        flow_id: this.idValue,
        event_type: 'flowpage:viewed',
        timestamp: Date.now(),
        user_data_snapshot: this.userDataValue as UserData,
        data: {
          new_page_id: pageId,
          new_page_key: currentPage.key,
          new_page_index: currentPageIndex,
          old_page_id: prevPageId,
          old_page_key: prevPage.key,
          old_page_index: prevPageIndex,
        },
      },
    ])
  }

  userDataValueChanged(userData: Record<string, unknown>) {
    /* Warning: Might cause infinite loops */

    log('userDataValueChanged() -> evaluateComputedFields()')
    this.evaluateComputedFields()

    this.upToDateUserData = userData

    document
      .querySelectorAll(`[data-controller="debug"]`)
      .forEach((el) => el.setAttribute('data-debug-user-data-value', JSON.stringify(userData)))

    /* Sets user data properties on all children "reactively" */
    if (typeof this.updateUserData === 'function') this.updateUserData()

    //* I update the neccesary preload Images
    if (this.flow.auto_preload_all_images) {
      const nextPageImagesSrc = this.getNextPageImagesSrc()
      this.preloadedImages = preloadPageImages(nextPageImagesSrc, this.preloadedImages)
    }
  }

  /* PUBLIC - These functions are meant to be called by event handlers or otherwise */
  //* Since dispatching actions with detail objects ends up passing actions array as key,
  //* we need to pass the triggerError to the dispatchAction function separately as well
  dispatchAction(
    key:
      | string
      | string[]
      | {
          detail: {
            actions: string | string[]
            component: BaseComponent
            callback?: Function
            triggerError?: TriggerError
          }
        },
    triggerType: string,
    currentPage?: Page,
    component?: BaseComponent,
    callback?: Function,
    triggerError?: TriggerError
  ) {
    //* Check for safe mode - skip action execution
    if (this.safeModeValue) {
      log('[Embeddables safe mode] Skipping action execution', key)
      return Promise.resolve()
    }

    /* Returns a promise that can be awaited */
    if (Array.isArray(key)) {
      return Promise.all(
        key.map((k) =>
          this.actions.dispatch(
            k,
            this.userDataValue,
            undefined,
            triggerType,
            component,
            currentPage,
            triggerError
          )
        )
      )
        .then(() => callback && callback())
        .catch(() => callback && callback('fail'))
    }

    //* Key could be a string on very old flows keeping this updated just for those cases
    if (typeof key === 'string') {
      return this.actions
        .dispatch(
          key,
          this.userDataValue,
          undefined,
          triggerType,
          component,
          currentPage,
          triggerError
        )
        .then(() => callback && callback())
        .catch(() => callback && callback('fail'))
    }

    if (key?.detail?.actions) {
      return this.dispatchAction(
        key.detail.actions,
        triggerType,
        currentPage,
        key?.detail?.component,
        key?.detail?.callback,
        key?.detail?.triggerError || triggerError
      )
    }

    return undefined
  }
  triggerFormValidation() {
    /* Assuming that if an infobox is open the user probably isn't interacting with the main flow */
    const target =
      (this.hasInfoboxTarget && this.infoboxTarget) ||
      (this.hasFormTarget && this.formTarget) ||
      null
    if (!target) {
      console.log('triggerFormValidation - no target found')
      return
    }

    if (this.flow.disable_form_validation) {
      //* This triggers custom UI validations
      const targets = target.querySelectorAll<CustomElement>('[custom-validity="true"]')
      targets.forEach((el) => {
        el.setAttribute('show-validity-message', 'true')
        setValidationMessage(el, el.customValidityMessage)
      })
    } else {
      //* This triggers native validations from the browser
      if (target.tagName === 'FORM') {
        if (typeof target.requestSubmit === 'function') {
          target.requestSubmit()
        } else {
          const submitButton = document.createElement('button')
          submitButton.type = 'submit'
          target.appendChild(submitButton)
          submitButton.click()
          submitButton.remove()
        }
      }
    }
  }

  async next(event?: { detail: { value: string; skipValidation?: boolean; isButton?: boolean } }) {
    /* Add a "next tick" promise that waits for any active user data updates before resolving */
    if (this.awaitingNext) return
    this.awaitingNext = true
    await this._pendingPromise
    /* Assume that infoBoxPageId is set if an infoBox is open, and unset if its closed */
    const pageId = this.infoBoxPageId || this.pageIdValue
    /* Skip if the event value does not match the current page */
    if (event?.detail?.value && pageId !== event.detail.value) {
      this.awaitingNext = false
      return
    }

    //* If the current page is an exit page, we need to stop the page from changing
    if (!this.isReactBuilder && this.flow.pages.find((p) => p.id === pageId)?.is_exit_page) {
      this.awaitingNext = false
      return
    }

    try {
      const skipValidation = event?.detail?.skipValidation
      const componentsAreValid = skipValidation ? true : this.getPageValidationStatus(pageId)
      if (componentsAreValid) {
        /* Trigger Actions for "completing" the current page */
        const currentPage = this.pagesById?.[pageId] || {}
        const actions = currentPage.outputs_oncomplete || []

        actions.forEach((id) => this.dispatchAction(id, 'outputs_oncomplete', currentPage))

        const allPages = this.flow.pages
        const indexInAllPages = allPages.findIndex((p) => p.id === this.pageIdValue)
        const futurePages = allPages.slice(indexInAllPages + 1)
        const futureVisiblePages = futurePages.filter((p) =>
          this.pages.find((_p) => p.id === _p.id)
        )
        if (futureVisiblePages.length) {
          // Go to the next visible page if there is one
          this.pageIdValue = futureVisiblePages[0].id
          const actionType = event?.detail?.isButton && this.syncPagesWithUrl ? 'push' : 'next'
          this.updatePageId(this.pageIdValue, actionType)

          this.updateComponentsValidationStatus()
        }
      } else {
        this.triggerFormValidation()
      }
    } catch (error) {
      console.error(error)
    }
    this.awaitingNext = false
  }

  prev(event?: { detail?: { isButton?: boolean } }) {
    //* Only apply protection if syncPagesWithUrl is active
    if (this.syncPagesWithUrl) {
      //* Check if transitions are active and prevent rapid navigation
      if (this.hasTransitionsActive() && this.awaitingPageChange) return
      if (this.hasTransitionsActive()) this.awaitingPageChange = true
    }

    const allPages = this.flow.pages
    const indexInAllPages = allPages.findIndex((p) => p.id === this.pageIdValue)
    const prevPages = allPages.slice(0, indexInAllPages)

    /**
     * Get all pages before the current page that are visible(ideally, this is
     * a slice of `this.pages`)
     */
    const prevVisiblePages = prevPages.filter((p) => this.pages.find((_p) => p.id === _p.id))
    const numVisiblePages = prevVisiblePages.length
    if (numVisiblePages > 0) {
      /**
       * Get the last visible page before the current page
       */
      const newPageId = prevVisiblePages[numVisiblePages - 1]?.id
      this.pageIdValue = newPageId
    }

    /**
     * If the current page is hidden, `pageIndex` always returned -1 before these
     * changes because the page was not found in the `this.pages` array(maintaining
     * behavior for backwards compatibility)
     */
    const pageIndex = allPages[indexInAllPages].hide ? -1 : numVisiblePages

    if (this.syncPagesWithUrl) {
      const history = this.getHistory()
      const isDifferentPage =
        !history.length || history?.[history.length - 1]?.flowPageIndex !== pageIndex - 1

      if (event?.detail?.isButton && isDifferentPage) {
        const newPage = this.pages[pageIndex - 1]
        window.history.replaceState(
          { flowPageKey: newPage.key, flowPageIndex: pageIndex - 1 },
          '',
          `#${newPage.key}`
        )
      }

      if (event?.detail?.isButton && history.length) {
        window.history.back()
      }
      this.updatePageId(this.pageIdValue, 'back')
    }
    //* Reset flag if no transitions (transitions will reset it when done)
    if (this.syncPagesWithUrl && !this.hasTransitionsActive()) {
      this.awaitingPageChange = false
    }
    return true
  }

  async reset(options?: ResetUserDataOptions) {
    this.setUserDataPromise()

    const newUserData = resetUserData(this.userDataValue, this.flow, options)

    this.userDataValue = {}
    if (!options?.preserveCurrentPage) this.pageIdValue = ''

    log('reset() -> evaluateComputedFields()')
    this.evaluateComputedFields(newUserData)

    this.batchUpdate({
      detail: {
        updates: newUserData,
        replace_user_data: true,
        immediate: true,
      },
    })

    await this._pendingPromise

    if (!options?.preserveCurrentPage) {
      if (this.pages[0]?.id) this.updatePageId(this.pages[0].id, 'reset')

      const pageEl = this.element.querySelector(`[data-controller~="page"]`)
      if (!pageEl) return console.warn('No page element found - not able to fully reset properly')
      const pageController = this.application.getControllerForElementAndIdentifier(pageEl, 'page')
      ;(pageController as any)?.resetAllPageHtmls?.()
    }
  }

  resetPage() {
    if (Array.isArray(this.pages) && this.pages.length) this.pageIdValue = this.pages[0].id
  }

  watchFlowUpdates(callback: (flow: Flow, prevFlow: Flow) => void): () => void {
    const self = this
    this.flowUpdateCallbacks.push(callback)

    return function () {
      const index = self.flowUpdateCallbacks.indexOf(callback)
      self.flowUpdateCallbacks.splice(index, 1)
    }
  }

  watchFlowUpdatesOnce(callback: (flow: Flow, prevFlow: Flow) => void): void {
    const self = this
    this.flowUpdateCallbacks.push((flow, prevFlow) => {
      const index = self.flowUpdateCallbacks.indexOf(callback)
      self.flowUpdateCallbacks.splice(index, 1)
      return callback(flow, prevFlow)
    })
  }

  watchFlowVersion(callback: (version: number, flow: Flow, branchId: string) => void): () => void {
    const self = this
    this.flowVersionCallbacks.push(callback)

    return function () {
      const index = self.flowVersionCallbacks.indexOf(callback)
      self.flowVersionCallbacks.splice(index, 1)
    }
  }

  goToPage(event: CustomEvent) {
    const idOrIndex = event?.detail?.value
    if (idOrIndex === undefined || idOrIndex === null) return
    const prevVisiblePageIndex = this.pages.findIndex((p) => p?.id === this.pageIdValue)
    const prevHiddenPageIndex = this.flow.pages.findIndex((p) => p?.id === this.pageIdValue)
    const prevIndex = prevVisiblePageIndex === -1 ? prevHiddenPageIndex : prevVisiblePageIndex

    const currentPage = this.flow.pages.find((p) => p.id === this.pageIdValue)
    const isExitPage = currentPage?.is_exit_page

    if (typeof idOrIndex === 'number') {
      if (0 < idOrIndex && idOrIndex < this.pages.length) {
        //* If the current page is an exit page, we need to check if the new page is after the exit page, and if so don't change the page
        if (!this.isReactBuilder && isExitPage && idOrIndex > prevHiddenPageIndex) return

        this.pageIdValue = this.pages[idOrIndex].id
      } else console.warn('Index not within flow pages')
    } else if (typeof idOrIndex === 'string') {
      //* If the current page is an exit page, we need to check if the new page is after the exit page, and if so don't change the page
      const newPageIndex = this.flow.pages.findIndex((p) => p.id === idOrIndex)
      if (!this.isReactBuilder && isExitPage && newPageIndex > prevHiddenPageIndex) return

      const normalPage = this.pages.find((page) => page.id === idOrIndex)
      const hiddenPage = this.flow.pages.find((page) => page.id === idOrIndex)

      // if it's a hidden page
      if (!normalPage && hiddenPage) {
        // if user is allowed to view hidden page
        if (this.userDataValue._show_hidden_pages) {
          this.pageIdValue = idOrIndex
          /**
           * If we only update the pageId, then the page will not be found
           * and `pageIdValueChanged` will have an early return. We need to
           * manually trigger `pageIdValueChanged` with the pageId prepended by
           * `hidden-` to ensure that the page is located in the `this.flow.pages`
           * array and the page is rendered.
           */
          this.pageIdValueChanged(`hidden-${idOrIndex}`, this.flow.pages[prevIndex].id)
        } else {
          // If user is not allowed to view hidden page, take user to the highest
          // possible page before the hidden page
          const allPages = this.flow.pages
          const indexInAllPages = allPages.findIndex((p) => p.id === idOrIndex)
          const prevPages = allPages.slice(0, indexInAllPages)
          const prevVisiblePages = prevPages.filter((p) => this.pages.find((_p) => p.id === _p.id))
          const numVisiblePages = prevVisiblePages.length

          if (numVisiblePages > 0) this.pageIdValue = this.pages[numVisiblePages - 1].id
        }
      } else {
        if (normalPage) {
          //* it's a normal (visible) page - only set pageIdValue if it exists in the flow
          this.pageIdValue = idOrIndex
        } else {
          console.warn(`Page id not found in flow, ignoring navigation: ${idOrIndex}`)
        }
      }
    }
    if (event?.detail?.value && this.syncPagesWithUrl) {
      const currIndex = this.pages.findIndex((p) => p.id === this.pageIdValue)

      if (currIndex > prevIndex) {
        //* Forward navigation: push all intermediate pages
        for (let i = prevIndex + 1; i <= currIndex; i++) {
          this.updatePageId(this.pages[i].id, 'push')
        }
      } else if (currIndex < prevIndex) {
        //* Backward navigation: update directly to target page
        this.updatePageId(this.pages[currIndex].id, 'push')
      }
    }
  }

  // All updates of pageIdValue should be done through this function, so that the browser history is updated correctly
  async updatePageId(pageId: string, actionType: 'back' | 'next' | 'reset' | 'push') {
    this.pageIdValue = pageId

    if (this.syncPagesWithUrl) {
      const pageIndex = this.pages.findIndex((p) => p.id === pageId)
      const page = this.pages[pageIndex]
      const prevPage = this.pages[pageIndex - 1]

      const prevState: State = prevPage
        ? {
            flowPageKey: prevPage.id,
            flowPageIndex: pageIndex - 1,
          }
        : undefined

      switch (actionType) {
        case 'back':
          this.popHistory()
          break
        case 'next':
          prevState && this.addToHistory(prevState)
          break
        case 'reset':
          sessionStorageRemove('SAVVY_WEB_HISTORY')
          window.history.pushState(
            { flowPageKey: page.key, flowPageIndex: pageIndex },
            '',
            `#${page.key}`
          )
          break
        case 'push':
          this.addToHistory(prevState)

          window.history.pushState(
            { flowPageKey: page.key, flowPageIndex: pageIndex },
            '',
            `#${page.key}`
          )
      }
    }
  }

  /* Exists as a backdoor for the case where InfoBox tries to open an InfoBox (siblings, so cannot access page methods) */
  async openInfoBox(event: CustomEvent) {
    // We wrapped `getOpenInfoBoxFn` in an empty Promise, so we need to await it before calling it
    ;(await getOpenInfoBoxFn(this))(event)
  }

  async closeInfoBox(event: CustomEvent) {
    ;(await getCloseInfoBoxFn(this))(event)
  }

  setInfoBoxPageId(event: CustomEvent) {
    const pageKey = event?.detail?.value
    const pages = this.flow.pages || []
    const page = pageKey ? pages.find((p) => p.key === pageKey) : null
    this.infoBoxPageId = page?.id || null
  }

  scrollToTop() {
    scrollToTop(this, this.flow || {})
    //* Move focus only when we scrolled the flow element so the form stays in view; skip when we scrolled the document (embed may be off-screen).
    if (!this.flow?.scroll_to_document_top) {
      //* Wait for the page to be updated before focusing the top of the page to avoid focusing on the wrong element
      window.addEventListener(
        'embeddables:page_change_html_updated',
        () => {
          const pageElement = this.element.querySelector('[data-controller~="page"]')
          if (pageElement) focusTopOfPage(pageElement)
        },
        { once: true }
      )
    }
  }

  /* Updates many properties at once, and sets them as prefilled if specified */
  batchUpdate(event: CustomEvent) {
    return new Promise((resolve, reject) => {
      try {
        const self = this
        const detail = event?.detail || {}
        const updates = detail.updates as Partial<UserData>
        if (!updates || !Object.keys(updates).length) {
          resolve(this.upToDateUserData)
          return
        }
        this.setUserDataPromise(detail?.immediate)

        this.upToDateUserData = { ...this.upToDateUserData, ...updates }

        const userData = updates || {}
        if (detail.prefill) {
          Object.keys(updates).forEach((key) => this.prefilledKeys.add(key))
          this.updatePrefilledKeys(userData)
        }
        log('batchUpdate() -> evaluateComputedFields()')
        this.evaluateComputedFields(userData).then(() => {
          if (detail?.immediate) self.reactivelyUpdateUserData()
          self.updateMany(updates, detail?.replace_user_data)
          resolve(this.upToDateUserData)
        })
      } catch (e) {
        reject(e)
      }
    })
  }

  /* Used for util function getSetUserDataFn */
  setUserData = (key: string, value: unknown, immediate?: boolean) => {
    if (typeof key === 'object') this.update({ detail: { updates: key, immediate } })
    else this.update({ params: { key, value }, target: { value }, detail: { immediate } })
  }

  /* Used for single updates */
  update(event: CustomEvent) {
    log('setUserData', JSON.stringify(event, null, 2))
    const self = this
    const { params, target, detail } = event
    this.setUserDataPromise(detail?.immediate)

    const userDataUpdate = {
      ...(params?.key
        ? { [params.key]: params.value || params.value === 0 ? params.value : target?.value }
        : {}),
      ...(detail?.key
        ? { [detail?.key]: detail.value || detail.value === 0 ? detail.value : target?.value }
        : {}),
      ...(detail?.updates ? detail.updates : {}),
    }

    this.upToDateUserData = { ...this.upToDateUserData, ...userDataUpdate }

    /* Sets User Data as part of the function */
    let hasGoneNext = false
    let recomputed = false
    /* Evaluate sync fields first since they generally finish faster, resulting in a faster user data update */
    log('update() -> evaluateComputedFields() sync')
    this.evaluateComputedFields(userDataUpdate, { async: false })
      .then(() => {
        if (!recomputed && detail?.immediate) {
          recomputed = true
          self.reactivelyUpdateUserData()
        }
        if (!hasGoneNext && detail?.goNext && detail.pageId) {
          hasGoneNext = true
          self.next({ detail: { isButton: true, value: detail?.pageId } })
        }
      })
      .then(() => {
        if (event.detail?.callback) event.detail.callback()
      })
      .finally(() => {
        // Update async field only after sync fields are done
        log('update() -> evaluateComputedFields() async')
        self.evaluateComputedFields(userDataUpdate, { sync: false })
      })
  }

  /* PRIVATE - Utility Methods */
  getPageValidationStatus(pageId: string) {
    const page = this.pagesById[pageId]
    if (page) {
      const pageComponents = page?.components || []
      return pageComponents.every((c) => {
        const res = componentPassesValidation(c, this.userDataValue)
        return res
      })
    } else {
      const flow = this.flow || {}
      const pages = flow.pages || []
      const rawPage = pages.find((p) => p.id === pageId)
      const pageComponents = rawPage?.components || []

      /* Filter components by conditions because this is a raw page object */
      const filteredComponents = pageComponents.filter(this.evaluateConditions)
      return filteredComponents.every((c) => componentPassesValidation(c, this.userDataValue))
    }
  }

  updatePrefilledKeys(userData: Record<string, unknown>) {
    const keys = Array.from(this.prefilledKeys).reduce(
      (acc, k) => ({ ...acc, [k]: this.userDataValue[k] || null }),
      {}
    )
    userData._prefilled = keys
  }

  setClassValues() {
    if (!this.flow) return
    const allKeys: Set<string> = new Set()
    const allowedComponentTypes = new Set(
      [...componentTypes()].filter((c) => c.group === 'User Input').map((c) => c.key)
    )

    if (Array.isArray(this.flow.pages)) {
      this.flow.pages.forEach((p) => {
        if (Array.isArray(p.components))
          p.components.forEach((c) => {
            if (c.set_value_class && allowedComponentTypes.has(c.type)) allKeys.add(c.key)
          })
      })
    }

    this.flow.computedFields?.forEach((c) => {
      if (c.set_value_class) allKeys.add(c.key)
    })

    const classes = Array.from(allKeys)
      .filter((k) => this.userDataValue[k] !== undefined)
      .map((k) => `field-value-${k}-${this.userDataValue[k]}`)

    const keepClasses = new Set(
      [
        `SavvyFlowPopup`,
        `Flow-Element`,
        `ElementType-FlowPopupOuter`,
        this.element.getAttribute('data-popup-opened-class'),
        this.element.getAttribute('data-popup-closed-class'),
      ].filter((e) => e)
    )
    const seenClasses: Set<string> = new Set()
    this.element.classList.forEach((classValue) =>
      keepClasses.has(classValue) ? seenClasses.add(classValue) : null
    )
    this.element.className = ''
    this.element.classList.add(...this.defaultClasses, ...seenClasses)
    if (classes.length > 0) this.element.classList.add(...classes)
  }

  async loadComputedFields() {
    //* Check for safe mode - skip computed fields loading
    if (this.safeModeValue) {
      log('[Embeddables safe mode] Skipping computed fields loading')
      return
    }

    const flow = this.flow || {}
    const computedFields = (flow.computedFields as ComputedFieldType[]) || []
    this.computedFields = computedFields.map((cf) => new ComputedField(cf, flow, this))
    await this.evaluateComputedFields({}, { sync: false })
    await this.evaluateComputedFields({}, { async: false })
    log('loadComputedFields() -> evaluateComputedFields()')
  }

  async evaluateComputedFields(
    update?: Record<string, unknown>,
    options: { sync?: boolean; async?: boolean } = {
      sync: true,
      async: true,
    }
  ) {
    //* Check for safe mode - skip computed fields evaluation
    if (this.safeModeValue) {
      log('[Embeddables safe mode] Skipping computed fields evaluation')
      return
    }

    const self = this
    const UPDATE_NUM = (this.UPDATE_NUM || 0) + 1
    this.UPDATE_NUM = UPDATE_NUM

    /**
     * Fetch the userDataValue on the next tick after the page has loaded
     */
    const before = await Promise.resolve().then(() => ({ ...self.userDataValue }))
    const userData: {} = { flow_version: this.versionValue, ...this.upToDateUserData, ...update }

    log(`-- evaluateComputedFields() SYNC PART #${UPDATE_NUM} userData, options`, userData, options)
    const { sync = true, async = true } = options

    //LUCIAN added following lines:
    // Add elements to this.computedFields from this.flow.computedFields if not there yet
    let count = this.flow.computedFields?.length || 0
    const flowComputedFields = this.flow.computedFields || []
    while (
      flowComputedFields.length &&
      flowComputedFields.length > this.computedFields?.length &&
      count !== 0
    ) {
      const flowField = flowComputedFields[count - 1] as ComputedFieldType
      let idExists = false
      for (let i = this.computedFields.length - 1; i >= 0; i--) {
        const item = this.computedFields[i]
        if (item?.fieldData.id === flowField.id) {
          idExists = true
          break
        }
      }
      if (!idExists) {
        let newCf = new ComputedField(flowField, this.flow, this)
        this.computedFields.push(newCf)
      }
      count--
    }
    // Remove elements from this.computedFields not present in this.flow.computedFields
    while (
      flowComputedFields.length &&
      flowComputedFields.length < this.computedFields?.length &&
      this.computedFields?.length > 0
    ) {
      const indexToRemove = this.computedFields.findIndex((item) => {
        return !flowComputedFields.some((flowItem) => flowItem.id === item.fieldData?.id)
      })
      if (indexToRemove !== -1) {
        this.computedFields.splice(indexToRemove, 1)
      }
    }
    //END of LUCIANs addition

    const computedFields = this.computedFields || []
    const evalId = Math.random()
    this.evalId = evalId
    const asyncComputedFields = computedFields.filter((cf) => {
      const field = cf.data()
      return field.async || field.code?.trim().startsWith('async function')
    })
    const asyncSet = new Set(asyncComputedFields)
    const syncComputedFields = computedFields.filter((cf) => !asyncSet.has(cf))

    const updateResults = (results: { key: string; result: unknown }[]) => {
      // Remove undefined/empty results
      const pendingUpdates = results.filter((r) => r)
      const userDataUpdate = pendingUpdates.reduce(
        (acc, result) => ({ ...acc, [result.key]: result.result }),
        update
      ) as Partial<UserData>
      log(
        `-- evaluateComputedFields() SYNC PART #${UPDATE_NUM} Updating User data:`,
        JSON.stringify(userDataUpdate || {}, null, 2)
      )
      self.updateMany(userDataUpdate)
    }

    // We update sync fields first because they are generally faster
    if (sync) {
      // Can we compute sync fields synchronously?
      const syncResults = await Promise.all(
        syncComputedFields.map(
          (field) =>
            field.compute(userData, before as UserData) as Promise<{
              result: unknown
              key: string
            }>
        )
      )
      updateResults(syncResults)
    }

    if (!async) return

    log(
      `-- evaluateComputedFields() ASYNC PART #${UPDATE_NUM} | evalId, this.evalId, evalId === this.evalId`,
      evalId,
      this.evalId,
      evalId === this.evalId
    )

    if (evalId === this.evalId && asyncComputedFields.length) {
      const asyncResults = await Promise.all(
        asyncComputedFields.map(
          (field) =>
            field.compute(userData, before as UserData) as Promise<{
              result: unknown
              key: string
            }>
        )
      )
      log(
        `-- evaluateComputedFields() ASYNC PART #${UPDATE_NUM} || evalId, this.evalId, evalId === this.evalId`,
        evalId,
        this.evalId,
        evalId === this.evalId
      )
      updateResults(asyncResults)
    }
  }

  evaluateConditionsForFlow() {
    const flow = this.flow || {}
    const pages = flow.pages || []
    const { evaluateConditions } = this

    const componentsAreValid = (components: Component[]) =>
      components.every((c) => componentPassesValidation(c, this.userDataValue))

    /* Evaluate conditions on each page */
    const visiblePages = pages.filter(evaluateConditions)

    this.pages = visiblePages.reduce((acc, page) => {
      const allComponents = page.components || []

      const components = allComponents
        .filter(evaluateConditions)
        .map((b) =>
          (b as OptionSelector)?.buttons
            ? { ...b, buttons: (b as OptionSelector).buttons?.filter(evaluateConditions) }
            : b
        )

      if (flow.hide_prefilled_pages) {
        const nonStaticComponentTypes = new Set(
          [...componentTypes()]
            .filter((c) => !['Layout', 'Static Display', 'Savvy Presets'].includes(c.group))
            .map((c) => c.key)
        )
        const nonStaticComponents = components.filter((c) => nonStaticComponentTypes.has(c.type))
        const allComponentsPrefilled =
          nonStaticComponents.length > 0
            ? nonStaticComponents.every((c) => this.prefilledKeys.has(c.key || c.id))
            : false
        const hidden = allComponentsPrefilled && componentsAreValid(nonStaticComponents)
        if (!hidden) acc.push({ ...page, components })
      } else acc.push({ ...page, components })

      return acc
    }, [] as Page[])
    this.pagesById = this.pages.reduce((acc, p) => {
      acc[p.id] = p
      return acc
    }, {})

    // if (this.userDataValue.current_page_id) this.pageIdValue = this.userDataValue.current_page_id

    /* If the page isn't in the allowed list, send the user back to the first page */
    if (
      this.hasConnected &&
      !this.pagesById[this.pageIdValue] &&
      !this.userDataValue._show_hidden_pages
      /**
       * A hidden page will never be on the allowed list, so when there is one we should
       * not use this verification. If we don't skip this check, we'll be automatically
       * sent back to the previous page
       */
    ) {
      const id = this.pages[0]?.id
      if (id) this.pageIdValue = id
    }
  }

  /* Use Babel Experimental Arrow Syntax because this is a callback and 'this' is a PITA */
  evaluateConditions = (p: Component | Page | OptionSelectorButton): boolean => {
    if (p.hide) return false
    return passesConditions(p.conditions || [], this.userDataValue)
  }

  reactivelyUpdateUserData({ bypassDebounce = false }: ReactivelyUpdateUserDataOptions = {}) {
    /* 
    All actions that take place upon updating user data go here - e.g.
    * Re-evaluating conditions
    * Sending userData down to child controllers
    */

    // console.log('Reactively updating data')

    //* Log the first reactive update only: compares what local storage holds against the in-memory user
    //* data we are about to persist. If the split key counts differ (split_key_mismatch), local storage
    //* and user data disagree about splits before this write, which may indicate a clobber or race.
    if (!this.hasLoggedFirstReactiveUpdate) {
      this.hasLoggedFirstReactiveUpdate = true
      const localStorageSplitKeyCount = getSplitKeyCountFromLocalStorage({ flowId: this.idValue })
      const userDataSplitKeyCount = countSplitKeys({ data: this.userDataValue })
      const splitKeyMismatch = localStorageSplitKeyCount !== userDataSplitKeyCount

      logSentryStep({
        scope: 'Flow controller',
        functionName: 'reactivelyUpdateUserData',
        step: 'split_persist_check',
        props: {
          flow_id: this.idValue,
          runtime_id: this.runtimeIdValue,
          user_data_split_key_count: userDataSplitKeyCount,
          local_storage_split_key_count: localStorageSplitKeyCount,
          split_key_mismatch: splitKeyMismatch,
        },
      })
    }

    this.setClassValues()
    this.evaluateConditionsForFlow()
    this.updateComponentsValidationStatus()
    if (!this.flow.do_not_store_any_data)
      writeUserData(this, this.userDataValue, { bypassDebounce })
    window.dispatchEvent(
      new CustomEvent('savvy:user_data_updated', {
        detail: { flow_id: this.idValue, user_data: this.userDataValue },
      })
    )

    window.dispatchEvent(
      new CustomEvent('embeddables:userdata_updated', {
        detail: { userdata: this.userDataValue },
      })
    )

    if (this._promiseActions) this._promiseActions.resolve()
  }

  setUserDataPromise(immediate?: boolean) {
    const self = this
    const promiseId = Math.random()
    this._promiseId = promiseId
    const timeoutDelay = immediate ? 0 : 250

    if (this._pendingPromise && this._promiseActions) this._promiseActions.resetTimeout()
    else
      this._pendingPromise = new Promise<void | boolean>((resolve, reject) => {
        const timeoutMgr = {
          timeoutId: setTimeout(() => resolve(), timeoutDelay),
        }
        const getTempTimeout = () => timeoutMgr.timeoutId

        this._promiseActions = {
          resetTimeout: () => {
            clearTimeout(getTempTimeout())
            timeoutMgr.timeoutId = setTimeout(() => resolve(), timeoutDelay)
          },
          resolve: () => {
            clearTimeout(getTempTimeout())
            resolve(true)
          },
          reject: () => {
            clearTimeout(getTempTimeout())
            reject(false)
          },
        }
      }).finally(() => {
        // if (this._promiseId === promiseId)
        self._promiseActions = null
      })
  }

  /* Used for batch updates */
  updateMany(update: Partial<UserData>, replace?: boolean) {
    const userData = replace ? update : { ...this.upToDateUserData, ...update }

    this.upToDateUserData = userData

    const hasAnalytics = typeof window.Savvy?.track === 'function'
    if (userData._has_analytics === undefined) userData._has_analytics = hasAnalytics
    /* Can be moved into first few lines of evaluateComputedFields if prefilled updating last is a problem */
    this.updatePrefilledKeys(userData)
    if (!isEqual(this.userDataValue, userData)) {
      updateFlowUserData(this, userData)
    }
  }

  popstateAction = (event: PopStateEvent): void => {
    if (!event?.state?.flowPageKey) return
    //* Only apply protection if syncPagesWithUrl is active
    if (this.syncPagesWithUrl) {
      //* Prevent rapid back navigation if transitions are active
      if (this.hasTransitionsActive() && this.awaitingPageChange) return
    }

    //* Obtain the current index from the visible pages array
    const localIndex = this.pages.findIndex((p) => p.id === this.pageIdValue)
    const currIndex = event.state.flowPageIndex

    const values = { detail: { skipValidation: true, value: this.pageIdValue } }

    if (localIndex > currIndex) this.prev()

    if (localIndex < currIndex) this.next(values)
  }

  addHistoryListener() {
    window.addEventListener('popstate', this.popstateAction)
  }

  removeHistoryListener() {
    window.removeEventListener('popstate', this.popstateAction)
  }

  //* Check if page has transitions enabled
  hasTransitionsActive(): boolean {
    const pageEl = this.element.querySelector(`[data-controller~="page"]`)
    if (!pageEl) return false
    const pageController = this.application.getControllerForElementAndIdentifier(
      pageEl,
      'page'
    ) as any
    //* Return true if enter or exit transitions are configured
    return pageController?.hasEnterClass || pageController?.hasExitClass || false
  }

  getHistory(i?: number): History {
    const history = JSON.parse(sessionStorageGet('SAVVY_WEB_HISTORY') || '[]')
    return i === undefined ? history : history.slice(i)[0]
  }

  setHistory(history: History) {
    sessionStorageSet('SAVVY_WEB_HISTORY', JSON.stringify(history))
  }

  popHistory() {
    const history = this.getHistory()
    history.pop()
    this.setHistory(history)
  }

  addToHistory(val: State) {
    const history = this.getHistory()
    history.push(val)
    this.setHistory(history)
  }

  switchToVersion({
    version,
    stopFlowRender,
    forcedJson,
    branchId,
  }: {
    version: number | string
    stopFlowRender?: boolean
    forcedJson?: Flow
    branchId?: string
  }) {
    // const qsArray = Object.entries(qsParams).map(([key, value]) => `${key}=${value}`)
    // const qs = qsArray.length > 0 ? `?${qsArray.join('&')}` : ''

    const query = new URLSearchParams(window.location.search)

    // Note: I wasn't sure if we should prioritize the urlParams or the version being passed by the function
    // in this scenario, so i left the function parameter as priority
    for (let key of query.keys()) {
      const value = query.get(key)
      if (key.split('_')[2] === this.idValue && value) {
        query.set('version', value)
      }
    }

    if (version) query.set('version', `${version}`)
    if (branchId) query.set('embeddable_branch', branchId)
    if (this.pageIdValue) query.set('current_page_id', this.pageIdValue)
    // Add savvy_editing=true if in editing mode
    if (isBuilderEditingMode(new URL(window.location.href))) query.set('savvy_editing', 'true')

    query.set('origin_url', window.location.href)

    const context: Context = {
      env: 'local',
      fetch: (...args: any[]) => fetch(args[0], args[1]),
      flowsBucket: {},
      flowsMetadataBucket: {},
      cmsR2Bucket: {},
    }
    const json = localStorageGet(`SAVVY_FLOW_JSON-${this.idValue}`) || forcedJson
    const request = {
      url: new URL(`${getRendererBase()}/${this.idValue}?${query.toString()}`),
      method: localStorageGet(`SAVVY_FLOW_JSON-${this.idValue}`) || forcedJson ? 'POST' : 'GET',
      headers: new Headers(env() === 'ci' ? { 'Host-Origin': window.location.origin } : {}),
      body: json ? { json } : null,
    }
    return handleRequest(context, request)
      .then((res) => JSON.parse(res.responseData))
      .then((response: any) => {
        const { html, flow } = response

        window.SavvyFlow = window.SavvyFlow || {}
        window.SavvyFlow[this.idValue] = flow

        const temporaryHTML = document.createElement('div')
        temporaryHTML.innerHTML = html

        document.querySelectorAll(`savvy[id*="${this.idValue}"]`).forEach((el) => {
          if (!stopFlowRender) {
            //* First I replace the style tag
            const styleTag = temporaryHTML.querySelector('style')
            if (styleTag) {
              el.querySelector('style')?.replaceWith(styleTag)
            }
          }

          //* Then I update all the flow component attributes, delete if theres one that is no loner in there and replace the innerHTML
          const flowComponent = temporaryHTML.querySelector(
            `[data-controller*="flow"][data-flow-id-value="${this.idValue}"]`
          )
          if (flowComponent) {
            let flowEl = el.querySelector(
              `[data-controller*="flow"][data-flow-id-value="${this.idValue}"]`
            )
            const isEditingMode = flowEl?.closest('savvy')?.className.includes('SavvySelectingMode')
            if (!stopFlowRender) {
              const flowComponentAttributes = flowComponent.attributes || []
              const attributesToMaintain = [
                'data-flow-edit-is-editing-flow-value',
                'data-flow-runtime-id-value',
                'data-action-suppressed',
                'data-action',
              ]

              for (let i = 0; i < flowComponentAttributes.length; i++) {
                const attr = flowComponentAttributes[i]
                //* I add this condition cause there's and attr buggy that brakes the loop (check if the attr starts with data)
                //* Also I need to maintain some attributes
                if (attr.name.startsWith('data') && !attributesToMaintain.includes(attr.name))
                  flowEl?.setAttribute(attr.name, attr.value)
              }

              const flowElAttributes = flowEl?.attributes || []
              for (let i = 0; i < flowElAttributes.length; i++) {
                const attr = flowElAttributes[i]
                if (attributesToMaintain.includes(attr.name)) continue
                if (!flowComponent.getAttribute(attr.name)) {
                  flowEl?.removeAttribute(attr.name)
                }
              }

              //* attr data-action should be added as data-action-suppressed also on the page element if I'm on editing mode
              if (isEditingMode) {
                const pageEl = temporaryHTML.querySelector(`[data-controller*="page"]`)
                if (pageEl) {
                  const action = pageEl.getAttribute('data-action')
                  if (action) pageEl.setAttribute('data-action-suppressed', action)
                  pageEl.removeAttribute('data-action')
                }
              }

              if (flowEl) flowEl.innerHTML = flowComponent.innerHTML
              else el.innerHTML = temporaryHTML.innerHTML
            }

            //* I update in flow-edit the rawSavedFlowJson & the currentVersion
            let flowEditController: any = null
            if (flowEl)
              flowEditController = this.application?.getControllerForElementAndIdentifier(
                flowEl,
                'flow-edit'
              )
            if (flowEditController) {
              flowEditController.rawSavedFlowJson = flow
              flowEditController.currentVersion =
                typeof version === 'number' ? version : parseInt(version)
              flowEditController.branchId = branchId
            }

            //* I update in json-update the originalFlowJson and the currentJson
            let jsonUpdateController: any = null
            if (flowEl)
              jsonUpdateController = this.application?.getControllerForElementAndIdentifier(
                flowEl,
                'json-update'
              )
            if (jsonUpdateController) {
              jsonUpdateController.originalFlowJson = flow
              jsonUpdateController.currentJson = flow
            }
          }
        })

        // @TODO: Make sure it's always the numerical version, not 'latest' etc
        this.flowVersionCallbacks.forEach((cb) => cb(version, flow))

        //* I clear the edit history
        const flowEditController = this.application?.getControllerForElementAndIdentifier(
          this.element,
          'flow-edit'
        ) as any

        if (flowEditController) flowEditController.clearEditHistory(true)

        //* I update the global-editor selectedFlowJson
        const savvyEditor = document.querySelector('savvy-editor')
        let globalEditorController: any = null
        if (savvyEditor) {
          globalEditorController = this.application?.getControllerForElementAndIdentifier(
            savvyEditor,
            'global-edit'
          )
          if (globalEditorController) globalEditorController.selectedFlowJson = flow
        }

        return true
      })
      .catch((error) => {
        console.error(`Error switching Flow ${this.idValue} to version ${version}:`, error)
        return false
      })
  }

  async getVersionHistory(limit?: number): Promise<{ versions: VersionMetadata[] }> {
    const supabaseClient = await initAndWaitForSupabaseClient()
    const versions = await supabaseClient
      .from('public_flow_versions')
      .select(
        'author_id, from_version_number, group_id, status, updated_at, version_number, label, edit_history_length' //"saved" and "status_history" are not in the public_flow_versions view - author_name & author_picture were removed when changing to the public view for privacy reasons
      )
      .eq('flow_id', this.idValue)
      .order('version_number', { ascending: false })
      .not('version_number', 'is', null)
      .range(0, limit ? limit - 1 : 24)
      .then(({ data, error }) => {
        if (error) {
          console.error(error)
          return []
        }
        return data.filter((doc) => doc.status !== 'DRAFT').slice(0, limit || 25)
      })
      .catch((error) => {
        console.error(error)
        return []
      })
    const versionsUpdated = versions.map((v) => {
      return {
        author: {
          userId: v.author_id,
        },
        groupId: v.group_id,
        id: `version-${v.version_number}`,
        saved: v.status === 'SAVED' || v.status === 'PROD' || v.status === 'STAGING' ? true : false, //not sure about this, in the old version returns true, not sure if always
        updatedAt: new Date(v.updated_at),
        version: v.version_number,
        branchId: v.branch_id,
        ...v,
      }
    })
    return { versions: versionsUpdated } // I wanted to return the versionsUpdated directly, but it was complaining and suggested this instead. Not sure if it would work though
  }

  updateComponentsValidationStatus() {
    const pageIsValid = this.getPageValidationStatus(this.pageIdValue)
    this.requirementsMetValue = pageIsValid
    this.element.querySelectorAll('[data-requirements-met]').forEach((el) => {
      const controllers = el.getAttribute('data-controller')
      controllers
        ?.split(' ')
        .filter((name) => CONTROLLERS_WITH_REQUIREMENTS.has(name))
        .forEach((controller) => {
          el.setAttribute(`data-${controller}-is-valid-value`, `${Boolean(pageIsValid)}`)
        })
    })
  }

  //* This gets next page images srcs and lotties too
  getNextPageImagesSrc() {
    let currentPageIndex = this.flow.pages.findIndex((p) => p.id === this.pageIdValue)
    let nextPage: Page | null = this.flow.pages?.[currentPageIndex + 1]
    if (this.getFlow) {
      let visibleFlow = this.getFlow() as Flow
      currentPageIndex = visibleFlow.pages.findIndex((p) => p.id === this.pageIdValue)
      nextPage = visibleFlow.pages?.[currentPageIndex + 1] as Page
    }
    let nextPageImagesSrc: string[] = []
    if (nextPage) {
      nextPage.components.forEach((component: Component) => {
        if (component.type === 'MediaImage' || component.type === 'Lottie') {
          if (component.src) nextPageImagesSrc.push(component.src)
        }

        //* find images inside option selector buttons and add them to the nextPageImagesSrc array
        if (component.type === 'OptionSelector') {
          ;(component as OptionSelector).buttons?.forEach((button: OptionSelectorButton) => {
            if (button.imageUrl) {
              nextPageImagesSrc.push(button.imageUrl)
            }
          })
        }

        //* find images inside customButtons and add them to the nextPageImagesSrc array
        if (component.type === 'CustomButton') {
          if ((component as CustomButton).imageUrl) {
            nextPageImagesSrc.push((component as CustomButton).imageUrl as string)
          }
        }
      })
    }
    //* I return filtering out duplicated srcs
    return [...new Set(nextPageImagesSrc)]
  }

  getStaticHtml(pageId: string, forceFlowJson?: Flow) {
    let origin: string =
      env() === 'dev' || env() === 'ci' ? 'http://localhost:8787' : window.location.origin
    // if (env() === 'ci') origin = origin + '/api'

    const flowToUse = forceFlowJson || this.flow
    const page = flowToUse.pages.find((p) => p.id === pageId)
    if (!page) return ''

    return generateBodyHtml(
      {
        origin,
        flowId: this.idValue,
        groupId: this.groupIdValue,
        projectId: this.projectIdValue,
        version: parseInt(this.versionValue),
        css: stylesGenerator(flowToUse, this.idValue, false),
        branchId: this.branchIdValue,
        userData: {},
        popup: false,
        editingMode: false,
        liteEditingMode: false,
        allowJsonUpdate: false,
        static_render: true,
        //* repeatables?: Component[]
        //* globalLocation?: string | null
      }, //* context
      false, //* pageContentOnly
      false, //* componentContentOnly
      flowToUse, //* flow
      page, //* page
      null //* components
      //* component?
    )
  }

  //TODO: Deprecated because of we're using view not table, and view doesn't support realtime updates
  async setUpLiveEditsChannelSubscription(previewUpdateMode: string, embeddableDraftId?: string) {
    const flowId = this.idValue
    const supabaseClient = await initAndWaitForSupabaseClient()

    supabaseClient
      .channel('new-versions-inserts')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'public_flow_versions',
          filter: `flow_id=eq.${this.idValue}`,
        },
        (payload: any) =>
          onNewVersionOrDraftInsertOrUpdate(payload, previewUpdateMode, embeddableDraftId)
      )
      .subscribe()

    supabaseClient
      .channel('draft-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'public_flow_versions',
          filter: `flow_id=eq.${this.idValue}`,
        },
        (payload: any) =>
          onNewVersionOrDraftInsertOrUpdate(payload, previewUpdateMode, embeddableDraftId)
      )
      .subscribe()

    supabaseClient
      .channel('drafts-deletes')
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'public_flow_versions',
          filter: `flow_id=eq.${this.idValue}`,
        },
        (payload) => onDraftDelete(payload, previewUpdateMode)
      )
      .subscribe()

    function onNewVersionOrDraftInsertOrUpdate(
      payload: any,
      previewUpdateMode: string,
      embeddableDraftId?: string
    ) {
      if (payload.new.status.toLowerCase() === 'saved') {
        handlePreviewUpdate('refresh')
      }
      if (payload.new.status.toLowerCase() === 'draft' && embeddableDraftId) {
        handlePreviewUpdate(previewUpdateMode)
      }
    }

    function handlePreviewUpdate(previewUpdateMode: string) {
      if (previewUpdateMode === 'refresh') {
        window.location.reload()
      }
      if (previewUpdateMode === 'rerender') {
        //* here we need to fetch the data from the new version and update the flow
        if (window.Savvy && window.Savvy.reRenderPage) {
          window.Savvy.reRenderPage(flowId)
        }
      }
    }

    function onDraftDelete(payload: any, previewUpdateMode: string) {
      handlePreviewUpdate(previewUpdateMode)
    }
  }
}

function shouldDisableSessionReplay(projectId: string) {
  switch (projectId) {
    // Disables session replay for LifeMD
    case 'pr_RU6v3fy1cVJEV6QT': {
      return true
    }
    default: {
      return false
    }
  }
}
